///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2026
//                           RXO Inc.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of RXO Inc. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { FixedSizeList as List } from "react-window";

import messages from "../../helpers/messages";
import OutlinedInput from "./OutlinedInput";

/** Above this length, facet options render in a react-window virtual list. */
export const FILTER_MENU_VIRTUALIZE_THRESHOLD = 100;

const ROW_HEIGHT = 32;
const MAX_LIST_PIXELS = 320;
const FALLBACK_VIRTUAL_LIST_WIDTH = 280;

/** Checkbox id — many option lists omit `key` and only define `value` (see confidenceTypeColorCodes). */
function filterMenuOptionCheckboxId(item) {
  if (item.key !== undefined && item.key !== null) {
    return item.key;
  }
  if (
    item.value !== undefined &&
    item.value !== null &&
    typeof item.value !== "object"
  ) {
    return item.value;
  }
  return undefined;
}

function findFilterOptionIndex(options, checkboxIdRaw) {
  const raw =
    checkboxIdRaw === undefined || checkboxIdRaw === null
      ? ""
      : String(checkboxIdRaw);
  const asNum = Number(raw);
  const numMatches =
    raw !== "" && !Number.isNaN(asNum) && Number.isFinite(asNum);

  let idx = options.findIndex(o => {
    if (o.key !== undefined && o.key !== null) {
      return numMatches
        ? o.key === asNum
        : String(o.key) === raw;
    }
    return false;
  });
  if (idx !== -1) {
    return idx;
  }
  return options.findIndex(o => {
    if (
      o.value !== undefined &&
      o.value !== null &&
      typeof o.value !== "object"
    ) {
      return numMatches
        ? o.value === asNum
        : String(o.value) === raw;
    }
    return false;
  });
}

function VirtualFilterRow({ index, style, data }) {
  const { options, filteredIndices, onItemChange } = data;
  const optionIndex =
    filteredIndices !== null ? filteredIndices[index] : index;
  const item = options[optionIndex];
  if (!item) {
    return <div style={style} />;
  }
  const checkboxId = filterMenuOptionCheckboxId(item);
  return (
    <div
      style={style}
      className="virtual-filter-row"
      onMouseUp={e => e.stopPropagation()}
    >
      <span>
        <input
          type="checkbox"
          id={checkboxId}
          checked={item.selected}
          onChange={onItemChange}
          className="mt-0 cursor-pointer"
          name={item.label}
        />
        <label className="align-top pl-8" htmlFor={checkboxId}>
          {item.label}
        </label>
      </span>
    </div>
  );
}

function FilterMenuContent({
  id,
  list,
  handleFilter,
  field,
  showApplyButton,
  isSearchable
}) {
  const [options, setOptions] = useState(list);
  const [searchTerm, setSearchTerm] = useState("");

  const useVirtualList = list.length > FILTER_MENU_VIRTUALIZE_THRESHOLD;

  useEffect(() => {
    setOptions(list);
    setSearchTerm("");
  }, [list]);

  const selectAllItem = {
    key: 0,
    selected: options.length > 0 && options.every(o => o.selected === true),
    label: messages.selectAll.defaultMessage
  };

  const handleSelectAll = useCallback(
    e => {
      const checked = e.target.checked;
      setOptions(prev => {
        const next = prev.map(o => ({ ...o, selected: checked }));
        if (!showApplyButton) {
          handleFilter(next, field);
        }
        return next;
      });
    },
    [showApplyButton, handleFilter, field]
  );

  const handleListItem = useCallback(
    e => {
      const checked = e.target.checked;
      setOptions(prev => {
        const idx = findFilterOptionIndex(prev, e.target.id);
        if (idx === -1) {
          return prev;
        }
        const next = [...prev];
        next[idx] = { ...prev[idx], selected: checked };
        if (!showApplyButton) {
          handleFilter(next, field);
        }
        return next;
      });
    },
    [showApplyButton, handleFilter, field]
  );

  const ListItem = ({ className, item, onChange }) => {
    const checkboxId = filterMenuOptionCheckboxId(item);
    return (
      <li className={className} onMouseUp={e => e.stopPropagation()}>
        <span>
          <input
            type="checkbox"
            id={checkboxId}
            key={checkboxId}
            checked={item.selected}
            onChange={onChange}
            className="mt-0 cursor-pointer"
            name={item.label}
          />
          <label className="align-top pl-8" htmlFor={checkboxId}>
            {item.label}
          </label>
        </span>
      </li>
    );
  };

  const filteredIndices = useMemo(() => {
    if (!searchTerm) {
      return null;
    }
    const out = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].label.toString().toLowerCase().includes(searchTerm)) {
        out.push(i);
      }
    }
    return out;
  }, [options, searchTerm]);

  const filteredCount =
    filteredIndices === null ? options.length : filteredIndices.length;

  const listPixelHeight = Math.min(
    filteredCount * ROW_HEIGHT,
    MAX_LIST_PIXELS
  );

  const virtualScrollRef = useRef(null);
  const [virtualWidth, setVirtualWidth] = useState(
    FALLBACK_VIRTUAL_LIST_WIDTH
  );

  useLayoutEffect(() => {
    if (!useVirtualList) {
      return;
    }
    const el = virtualScrollRef.current;
    if (!el) {
      return;
    }
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      setVirtualWidth(
        Number.isFinite(w) && w > 0 ? Math.floor(w) : FALLBACK_VIRTUAL_LIST_WIDTH
      );
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => ro && ro.disconnect();
  }, [useVirtualList, list, filteredCount]);

  const virtualListData = useMemo(
    () => ({
      options,
      filteredIndices,
      onItemChange: handleListItem
    }),
    [options, filteredIndices, handleListItem]
  );

  const virtualItemKey = useCallback((index, data) => {
    const optionIndex =
      data.filteredIndices !== null ? data.filteredIndices[index] : index;
    const opt = data.options[optionIndex];
    const idPart = filterMenuOptionCheckboxId(opt);
    return idPart !== undefined && idPart !== null
      ? idPart
      : `virtual-row-${optionIndex}`;
  }, []);

  const filteredOptionsNonVirtual = options.filter(x =>
    !searchTerm ||
    x.label.toString().toLowerCase().includes(searchTerm)
  );

  return (
    <div id="filter-menu-content">
      {isSearchable && (
        <OutlinedInput
          className="truncate"
          data-testid="search-input"
          onChange={({ target }) =>
            setSearchTerm(target.value.toLowerCase().trim())
          }
          onMouseUp={e => e.stopPropagation()}
        />
      )}
      {useVirtualList ? (
        <>
          <ul className="mb-0" onMouseUp={e => e.stopPropagation()}>
            <ListItem
              className="border-bottom-grey"
              item={selectAllItem}
              onChange={handleSelectAll}
            />
          </ul>
          <div
            ref={virtualScrollRef}
            className="mb-0"
            id={id}
            style={{
              maxHeight: MAX_LIST_PIXELS,
              width: "100%",
              overflow: "hidden"
            }}
          >
            {filteredCount > 0 ? (
              <List
                className="virtual-filter-list"
                height={listPixelHeight}
                width={virtualWidth}
                itemCount={filteredCount}
                itemSize={ROW_HEIGHT}
                itemData={virtualListData}
                itemKey={virtualItemKey}
              >
                {VirtualFilterRow}
              </List>
            ) : null}
          </div>
        </>
      ) : (
        <ul className="mb-0 scroll" id={id} onScroll={e => e.stopPropagation()}>
          <ListItem
            className="border-bottom-grey"
            item={selectAllItem}
            onChange={handleSelectAll}
          />
          {filteredOptionsNonVirtual.map((o, i) => (
            <ListItem
              item={o}
              onChange={handleListItem}
              key={`${String(filterMenuOptionCheckboxId(o) ?? "opt")}-${i}`}
            />
          ))}
        </ul>
      )}
      {showApplyButton && (
        <button
          className="text-button univers-67-bold-cond font-size-14 uppercase pt-4"
          data-testid="apply-button"
          style={{ minWidth: 50 }}
          onClick={() => handleFilter(options, field)}
          onMouseUp={e => e.stopPropagation()}
        >
          {messages.apply.defaultMessage}
        </button>
      )}
    </div>
  );
}

export default FilterMenuContent;
